import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatGs, LOCATION_NAMES } from "@/lib/format";
import { PageHeader, Input, Modal, Btn } from "@/components/ui/kit";
import { toast } from "sonner";
import { TicketModal } from "@/components/TicketModal";
import { MyShiftModal } from "@/components/MyShiftModal";
import { HandoverModal } from "@/components/HandoverModal";
import { LowStockPanel, isLowStock } from "@/components/LowStockPanel";
import { Search, Plus, Minus, Trash2, ShoppingCart, Package, Banknote, CreditCard, ArrowLeftRight, AlertTriangle, Wallet, Lock, Unlock, ArrowDownToLine, Sliders, ClipboardList, Repeat } from "lucide-react";

const PAY_METHODS = [
  { key: "efectivo", label: "Efectivo", tag: "EN MANO", color: "text-emerald-500", Icon: Banknote },
  { key: "tarjeta", label: "POS", tag: "TARJETA", color: "text-blue-500", Icon: CreditCard },
  { key: "transferencia", label: "Transfer", tag: "AMORTIZACIÓN", color: "text-purple-500", Icon: ArrowLeftRight },
];

export default function POS() {
  const { location, user } = useAuth();
  const isAdmin = user?.role === "super-admin" || user?.role === "administrador";
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("todos");
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [payments, setPayments] = useState({ efectivo: 0, tarjeta: 0, transferencia: 0 });
  const [discount, setDiscount] = useState(0);
  const [cash, setCash] = useState(null);
  const [modal, setModal] = useState(null); // open | close | retiro | ajuste
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [myShift, setMyShift] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [settings, setSettings] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/products", { params: { location } });
      setProducts(data);
      setCats(["todos", ...Array.from(new Set(data.map((p) => p.category)))]);
      return data;
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadCash = async () => {
    try { const { data } = await api.get("/cash/current", { params: { location } }); setCash(data); }
    catch (e) { /* silent */ }
  };
  useEffect(() => { load(); loadCash(); setCart([]); }, [location, user?.id]);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => products.filter((p) =>
    (p[`stock_${location}`] || 0) > 0 &&
    (cat === "todos" || p.category === cat) && (p.name.toLowerCase().includes(search.toLowerCase()) || (p.barcode && p.barcode === search.trim()))
  ), [products, cat, search, location]);

  const onSearchKey = (e) => {
    if (e.key !== "Enter") return;
    const code = search.trim();
    if (!code) return;
    const hit = products.find((p) => p.barcode && p.barcode === code) || (filtered.length === 1 ? filtered[0] : null);
    if (hit) { addToCart(hit); setSearch(""); } else toast.error("Código no encontrado: " + code);
  };

  const stockOf = (p) => p[`stock_${location}`] || 0;

  const addToCart = (p) => {
    if (stockOf(p) <= 0) { toast.error("Sin stock en " + LOCATION_NAMES[location]); return; }
    setCart((c) => {
      const ex = c.find((x) => x.product_id === p.id);
      if (ex) return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { product_id: p.id, name: p.name, price: p.price, quantity: 1 }];
    });
  };
  const changeQty = (id, d) => setCart((c) => c.map((x) => x.product_id === id ? { ...x, quantity: Math.max(1, x.quantity + d) } : x));
  const removeItem = (id) => setCart((c) => c.filter((x) => x.product_id !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const total = Math.max(0, subtotal - (Number(discount) || 0));
  const paid = Object.values(payments).reduce((s, v) => s + (Number(v) || 0), 0);
  const diff = Math.round(total - paid);          // >0 falta
  const change = Math.max(0, Math.round(paid - total)); // vuelto
  const canConfirm = cart.length > 0 && paid >= total - 0.5; // permitir cobrar si el monto es igual o superior al total

  const confirmSale = async () => {
    if (!cash?.open) { toast.error("Abrí tu caja para poder vender"); return; }
    if (!cart.length) { toast.error("Cargue productos al carrito"); return; }
    if (!canConfirm) { toast.error(`Falta cobrar ${formatGs(diff)}`); return; }
    try {
      const res = await api.post("/sales", { location, items: cart, payments, discount: Number(discount) || 0 });
      toast.success(change > 0 ? `Venta registrada · Vuelto ${formatGs(res.data.change)}` : "Venta registrada: " + formatGs(total));
      setTicket(res.data);
      setCart([]); setDiscount(0); setPayments({ efectivo: 0, tarjeta: 0, transferencia: 0 });
      loadCash();
      const sold = new Set(cart.map((i) => i.product_id));
      const fresh = await load();
      (fresh || []).filter((p) => sold.has(p.id) && isLowStock(p, location)).forEach((p) => {
        const st = p[`stock_${location}`] || 0;
        toast.warning(st <= 0 ? `${p.name} se agotó en ${LOCATION_NAMES[location]}: reponer` : `Poco stock: ${p.name} (quedan ${st}) — reponer`, { duration: 6000 });
      });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openModal = (type) => { setModal(type); setAmount(""); setReason(""); setAuthEmail(""); setAuthPass(""); };

  const submitCash = async () => {
    try {
      if (modal === "open") {
        await api.post("/cash/open", { location, opening_amount: Number(amount) || 0 });
        toast.success("Caja abierta");
      } else if (modal === "close") {
        const { data } = await api.post("/cash/close", { location, counted_amount: Number(amount) || 0, notes: reason });
        toast.success(`Caja cerrada · Diferencia ${formatGs(data.difference)}`);
        if (data.alert) toast.warning("La diferencia supera la tolerancia: se notificó al administrador");
        window.dispatchEvent(new Event("cash-closed"));
      } else {
        await api.post("/cash/movement", { location, type: modal, amount: Number(amount) || 0, reason, ...(isAdmin ? {} : { auth_email: authEmail, auth_password: authPass }) });
        toast.success(modal === "retiro" ? "Retiro registrado" : "Ajuste registrado");
      }
      setModal(null); loadCash();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const expected = cash?.open ? cash.expected_cash : null;

  return (
    <div>
      <PageHeader title="Punto de Venta" subtitle={`Local activo: ${LOCATION_NAMES[location]}`}>
        <LowStockPanel products={products} location={location} onChanged={load} />
        {/* Caja: efectivo esperado */}
        <div className={`flex items-center gap-3 rounded-xl border px-4 h-11 ${cash?.open ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-secondary"}`} data-testid="cash-badge">
          <Wallet className={`w-5 h-5 ${cash?.open ? "text-emerald-500" : "text-muted-foreground"}`} />
          <div className="leading-none">
            <p className="text-[9px] font-bold tracking-widest text-muted-foreground">MI EFECTIVO EN CAJA</p>
            <p className="font-data font-bold text-sm" data-testid="cash-expected">{cash?.open ? formatGs(expected) : "Caja cerrada"}</p>
          </div>
        </div>
        {cash?.open ? (
          <>
            <Btn variant="outline" onClick={() => setMyShift(true)} data-testid="my-shift-btn"><ClipboardList className="w-4 h-4" /> Mi caja</Btn>
            <Btn variant="outline" onClick={() => openModal("retiro")} data-testid="cash-retiro-btn"><ArrowDownToLine className="w-4 h-4" /> Retiro</Btn>
            {isAdmin && <Btn variant="outline" onClick={() => openModal("ajuste")} data-testid="cash-ajuste-btn"><Sliders className="w-4 h-4" /> Ajuste</Btn>}
            <Btn variant="outline" onClick={() => setModal("handover")} data-testid="cash-handover-btn"><Repeat className="w-4 h-4" /> Traspasar</Btn>
            <Btn variant="danger" onClick={() => openModal("close")} data-testid="cash-close-btn"><Lock className="w-4 h-4" /> Cerrar Caja</Btn>
          </>
        ) : (
          <>
            <Btn variant="outline" onClick={() => setMyShift(true)} data-testid="my-shift-btn"><ClipboardList className="w-4 h-4" /> Mi caja</Btn>
            <Btn onClick={() => openModal("open")} data-testid="cash-open-btn"><Unlock className="w-4 h-4" /> Abrir Caja</Btn>
          </>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:h-[calc(100vh-12.5rem)]">
        {/* Products */}
        <div className="lg:col-span-7 lg:overflow-y-auto lg:pr-2 overscroll-contain" data-testid="pos-products-scroll">
          <div className="lg:sticky lg:top-0 z-10 bg-background pb-1">
          <div className="relative mb-4">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onSearchKey} data-testid="pos-search" autoFocus
              placeholder="Buscar producto o escanear código de barras…"
              className="w-full h-10 pl-9 pr-3 rounded-lg bg-secondary border border-border focus:border-primary focus:outline-none" />
          </div>
          <div className="flex gap-2 mb-4 flex-wrap">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`pos-cat-${c}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                {c}
              </button>
            ))}
          </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.map((p) => {
              const st = stockOf(p);
              const low = isLowStock(p, location);
              return (
                <button key={p.id} onClick={() => addToCart(p)} disabled={st <= 0} data-testid={`pos-product-${p.id}`}
                  className="card-surface overflow-hidden text-left hover:border-primary hover:neon-glow transition-all disabled:opacity-40 group">
                  <div className="aspect-[4/3] bg-secondary overflow-hidden relative">
                    {p.is_combo && <span className="absolute top-1.5 left-1.5 z-10 text-[9px] font-bold px-1.5 py-0.5 rounded bg-primary text-primary-foreground">COMBO</span>}
                    {low && <span className="absolute top-1.5 right-1.5 z-10 text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-black" data-testid={`low-badge-${p.id}`}>¡QUEDAN {st}!</span>}
                    {p.image_url ? <img src={fileUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      : <div className="w-full h-full flex items-center justify-center"><Package className="w-8 h-8 text-muted-foreground" /></div>}
                  </div>
                  <div className="p-2.5">
                    <p className="text-sm font-semibold truncate">{p.name}</p>
                    <p className="font-data text-primary font-bold text-sm mt-1">{formatGs(p.price)}</p>
                    <p className={`text-[10px] mt-0.5 ${low ? "text-amber-500 font-semibold" : "text-muted-foreground"}`}>Stock: {st}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right column: Cobro arriba, Carrito abajo */}
        <div className="lg:col-span-5 lg:overflow-y-auto overscroll-contain lg:pr-1" data-testid="pos-cart-scroll">
          <div className="space-y-4">
            {/* COBRO (arriba) */}
            <div className="card-surface p-5" data-testid="payment-panel">
              <div className="rounded-xl bg-primary p-4 text-primary-foreground neon-glow mb-3">
                <p className="text-[10px] font-bold tracking-widest opacity-80">TOTAL A COBRAR</p>
                <p className="font-data text-3xl font-bold" data-testid="total-display">{formatGs(total)}</p>
                {subtotal > 0 && (
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/20 text-xs">
                    <span className="opacity-80">Descuento</span>
                    <input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="sale-discount"
                      className="w-28 h-7 px-2 rounded-md bg-white/15 text-primary-foreground text-right font-data placeholder-white/50 focus:outline-none" placeholder="0" />
                  </div>
                )}
              </div>
              {PAY_METHODS.map(({ key, label, tag, color, Icon }) => (
                <div key={key} className="space-y-1.5 mb-2.5">
                  <div className="flex items-center justify-between">
                    <span className={`flex items-center gap-1.5 text-sm font-semibold ${color}`}><Icon className="w-4 h-4" /> {label}</span>
                    <span className="text-[9px] font-bold tracking-widest text-muted-foreground">{tag}</span>
                  </div>
                  <div className="flex gap-2">
                    <input type="number" min="0" value={payments[key] || ""} placeholder="0"
                      onChange={(e) => setPayments({ ...payments, [key]: Math.max(0, Number(e.target.value) || 0) })}
                      data-testid={`pay-input-${key}`}
                      className="flex-1 h-10 px-4 rounded-full bg-secondary border border-border font-data text-sm focus:border-primary focus:outline-none" />
                    <button onClick={() => setPayments({ ...payments, [key]: Math.max(0, (Number(payments[key]) || 0) + Math.max(0, diff)) })} data-testid={`pay-exact-${key}`}
                      className="h-10 px-4 rounded-full border border-primary/50 text-primary text-xs font-bold hover:bg-primary/10 transition-colors">Exacto</button>
                  </div>
                  {key === "efectivo" && (
                    <div className="flex gap-1.5 flex-wrap items-center">
                      <button onClick={() => setPayments({ ...payments, efectivo: 0 })} data-testid="pay-cash-clear" title="Limpiar"
                        className="w-8 h-8 rounded-full border border-destructive/50 text-destructive text-sm font-bold hover:bg-destructive/10 transition-colors">−</button>
                      {[5000, 10000, 20000, 50000, 100000].map((v) => (
                        <button key={v} onClick={() => setPayments({ ...payments, efectivo: (Number(payments.efectivo) || 0) + v })} data-testid={`cash-chip-${v}`}
                          className="h-8 px-3 rounded-full border border-emerald-500/50 text-emerald-500 text-[11px] font-bold hover:bg-emerald-500/10 transition-colors">Gs. {v.toLocaleString("es-PY")}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {change > 0 && (
                <div className="flex items-center justify-between h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 mb-2" data-testid="change-display">
                  <span className="text-sm font-semibold text-emerald-500">Vuelto</span>
                  <span className="font-data font-bold text-emerald-500">{formatGs(change)}</span>
                </div>
              )}
              <button onClick={confirmSale} disabled={!canConfirm} data-testid="pos-checkout"
                className={`w-full h-12 rounded-full font-head font-extrabold tracking-wide transition-colors ${canConfirm ? "bg-primary text-primary-foreground neon-glow hover:bg-primary/90" : "bg-primary/40 text-primary-foreground/70 cursor-not-allowed"}`}>
                CONFIRMAR VENTA
              </button>
              {(cart.length === 0 || diff > 0) && (
                <div className="flex items-center justify-center gap-2 h-10 rounded-xl border border-amber-500/40 text-amber-500 text-xs font-medium mt-2" data-testid="pay-warning">
                  <AlertTriangle className="w-4 h-4" />
                  {cart.length === 0 ? "Cargue productos al carrito" : `Falta cobrar ${formatGs(diff)}`}
                </div>
              )}
            </div>

            {/* CARRITO (abajo, crece hacia abajo) */}
            <div className="card-surface p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShoppingCart className="w-5 h-5 text-primary" />
                <h3 className="font-head font-bold text-lg">Carrito</h3>
                <span className="ml-auto text-xs text-muted-foreground">{cart.length} items · {formatGs(subtotal)}</span>
              </div>
              <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                {cart.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Carrito vacío</p>}
                {cart.map((i) => (
                  <div key={i.product_id} className="flex items-center gap-2 bg-secondary rounded-lg p-2 animate-fade-up" data-testid={`cart-item-${i.product_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{i.name}</p>
                      <p className="font-data text-xs text-muted-foreground">{formatGs(i.price)} · {formatGs(i.price * i.quantity)}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => changeQty(i.product_id, -1)} data-testid={`cart-dec-${i.product_id}`} className="w-6 h-6 rounded bg-background flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                      <span className="w-6 text-center text-sm font-semibold">{i.quantity}</span>
                      <button onClick={() => changeQty(i.product_id, 1)} data-testid={`cart-inc-${i.product_id}`} className="w-6 h-6 rounded bg-background flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                    </div>
                    <button onClick={() => removeItem(i.product_id)} data-testid={`cart-remove-${i.product_id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <TicketModal sale={ticket} settings={settings} onClose={() => setTicket(null)} />
      <MyShiftModal open={myShift} onClose={() => setMyShift(false)} location={location} cash={cash} onReprint={(s) => { setMyShift(false); setTicket(s); }} />
      <HandoverModal open={modal === "handover"} onClose={() => setModal(null)} location={location} cash={cash} onDone={() => setModal(null)} />

      {/* Cash modals */}
      <Modal open={modal === "open"} onClose={() => setModal(null)} title="Abrir caja">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Ingresá el monto inicial de efectivo con el que abrís la caja en {LOCATION_NAMES[location]}.</p>
          <div><label className="text-sm font-medium mb-1 block">Monto inicial (Gs.)</label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cash-open-amount" autoFocus /></div>
          <div className="flex gap-2"><Btn variant="outline" onClick={() => setModal(null)} className="flex-1">Cancelar</Btn><Btn onClick={submitCash} data-testid="cash-open-submit" className="flex-1">Abrir Caja</Btn></div>
        </div>
      </Modal>

      <Modal open={modal === "close"} onClose={() => setModal(null)} title="Cerrar caja · Arqueo">
        <div className="space-y-4">
          {cash?.open && (
            <div className="rounded-lg bg-secondary p-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Monto inicial</span><span className="font-data">{formatGs(cash.opening_amount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ventas en efectivo</span><span className="font-data">{formatGs(cash.cash_sales)}</span></div>
              {cash.ingresos > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Ingresos</span><span className="font-data">{formatGs(cash.ingresos)}</span></div>}
              {cash.ajustes !== 0 && <div className="flex justify-between"><span className="text-muted-foreground">Ajustes</span><span className="font-data">{formatGs(cash.ajustes)}</span></div>}
              {cash.retiros > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Retiros</span><span className="font-data text-destructive">-{formatGs(cash.retiros)}</span></div>}
              <div className="flex justify-between pt-2 border-t border-border font-bold"><span>Efectivo esperado</span><span className="font-data text-primary">{formatGs(cash.expected_cash)}</span></div>
            </div>
          )}
          <div><label className="text-sm font-medium mb-1 block">Efectivo contado (arqueo real)</label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cash-close-amount" autoFocus /></div>
          {amount !== "" && cash?.open && (
            <p className="text-sm">Diferencia: <span className={`font-data font-bold ${Number(amount) - cash.expected_cash === 0 ? "text-emerald-500" : "text-amber-500"}`}>{formatGs(Number(amount) - cash.expected_cash)}</span></p>
          )}
          <div><label className="text-sm font-medium mb-1 block">Notas (opcional)</label><Input value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <div className="flex gap-2"><Btn variant="outline" onClick={() => setModal(null)} className="flex-1">Cancelar</Btn><Btn variant="danger" onClick={submitCash} data-testid="cash-close-submit" className="flex-1">Cerrar y Guardar Arqueo</Btn></div>
        </div>
      </Modal>

      <Modal open={modal === "retiro" || modal === "ajuste"} onClose={() => setModal(null)} title={modal === "retiro" ? "Retiro de efectivo" : "Ajuste de caja"}>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {modal === "retiro" ? "Registra una extracción de efectivo de la caja (reduce el efectivo esperado)." : "Corrige el efectivo esperado. Usá valores negativos para descontar."}
          </p>
          <div><label className="text-sm font-medium mb-1 block">Monto (Gs.)</label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cash-mov-amount" autoFocus /></div>
          <div><label className="text-sm font-medium mb-1 block">Motivo</label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cash-mov-reason" placeholder={modal === "retiro" ? "Ej: pago a proveedor" : "Ej: corrección de conteo"} /></div>
          {!isAdmin && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="cash-auth-box">
              <p className="text-xs font-semibold text-amber-500">Autorización de un administrador</p>
              <Input type="email" autoComplete="off" value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} placeholder="Correo del administrador" data-testid="cash-auth-email" />
              <Input type="password" autoComplete="new-password" value={authPass} onChange={(e) => setAuthPass(e.target.value)} placeholder="Contraseña del administrador" data-testid="cash-auth-password" />
            </div>
          )}
          <div className="flex gap-2"><Btn variant="outline" onClick={() => setModal(null)} className="flex-1">Cancelar</Btn><Btn onClick={submitCash} data-testid="cash-mov-submit" className="flex-1">Registrar</Btn></div>
        </div>
      </Modal>
    </div>
  );
}
